import { tC } from "../../utils/TranslateWithContentLanguage";
import { Clock, Calculator, RefreshCw } from "lucide-react";
import CardImage from "../tool_components/CardImage";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import { Button, Text } from "@core/index";
import RubricCardPDF from "./RubricCardPdf";
import Markdown from "react-markdown";
import { RubricGeneratorResponseDto } from "@packages/api/madlenAPI.schemas";
import { useAppSelector } from "@packages/store/hooks";
import { useState } from "react";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";

export const RubricCard = ({
  rubric,
  isImageLoading,
  onCreateAgain,
}: {
  rubric: RubricGeneratorResponseDto;
  isImageLoading: boolean;
  onCreateAgain: () => void;
}) => {
  const finalImageUrl = useImagePdf(rubric.imageUrl);
  const contentLanguage = rubric.contentLanguage || "tr";
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  return (
    <div className="space-y-6">
      {/* Header Section */}
      <CardImage
        createdAt={rubric.createdAt}
        imageUrl={rubric.imageUrl}
        videoUrl={rubric.videoUrl}
        courseName={rubric.courseName}
        gradeLevel={Number(rubric.gradeLevel) || 0}
        theme="amber"
        isLoading={isImageLoading}
        topic={rubric.title || rubric.prompt}
        contentLanguage={contentLanguage}
        badges={
          <>
            <div className="flex items-center space-x-2 bg-[#7EA88E]/90 px-3 py-1.5 rounded-full">
              <Calculator size={14} className="text-white" />
              <Text
                variant="span"
                size="sm"
                weight="medium"
                className="text-white"
              >
                {tC(contentLanguage, "rubric.form.totalScore")}:{" "}
                {rubric.totalScore}
              </Text>
            </div>
          </>
        }
      />
      <div className="flex justify-end gap-2 mt-2">
        {onCreateAgain && (
          <Button
            onClick={onCreateAgain}
            variant="solid"
            size="md"
            textColor="text-white dark:text-white"
            bgColor="bg-gradient-to-r from-[#7EA88E] to-[#4A7759] hover:from-[#7EA88E]/90 hover:to-[#4A7759]/90"
            rounded="xl"
          >
            <RefreshCw size={18} />
            <span>{tC(contentLanguage, "common.createAnother")}</span>
          </Button>
        )}
        <PdfDownloadButton
          pdfTitle={rubric.title || rubric.prompt || ""}
          document={
            <RubricCardPDF
              customizationSettings={(customizationSettings as any) || {}}
              rubric={{ ...rubric, imageUrl: finalImageUrl }}
            />
          }
          btnTheme="bg-gradient-to-r from-[#7EA88E] to-[#4A7759]"
          openCustomizationModal={() => setShowPdfCustomizationModal(true)}
        />
      </div>
      {/* Rubric Type */}
      <div className="bg-[#E8F1EB] dark:bg-[#2A3E33]/20 p-4 rounded-lg">
        <Text variant="p" className="text-[#4A7759] dark:text-[#98C4A8]">
          {rubric.description}
        </Text>
      </div>
      {/* Criteria Cards Section - ANALYTIC */}
      {rubric.rubricType === "ANALYTIC" && rubric.criteria && (
        <div className="space-y-6">
          {rubric.criteria.map((criterion, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm"
            >
              {/* Criterion Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1">
                  <Text
                    variant="h3"
                    size="lg"
                    weight="semibold"
                    className="text-gray-900 dark:text-gray-100 mb-2"
                  >
                    <Markdown>{criterion.criterion}</Markdown>
                  </Text>
                </div>
                <div className="ml-4 flex items-center space-x-2 bg-[#7EA88E]/10 px-3 py-1.5 rounded-full">
                  <Text
                    variant="span"
                    size="sm"
                    weight="medium"
                    className="text-[#4A7759] dark:text-[#98C4A8]"
                  >
                    {tC(contentLanguage, "rubric.weight")}: {criterion.weight}%
                  </Text>
                </div>
              </div>

              {/* Levels Grid */}
              {(() => {
                const descriptorCount =
                  criterion.level_descriptors?.length || 0;
                let gridCols = "grid-cols-1";
                let mdGridCols = "md:grid-cols-2";
                let lgGridCols = "lg:grid-cols-3";
                let xlGridCols = "";

                // Determine grid columns based on descriptor count
                if (descriptorCount === 7) {
                  // 4 columns for 7
                  gridCols = "grid-cols-1";
                  mdGridCols = "md:grid-cols-2";
                  lgGridCols = "lg:grid-cols-3";
                  xlGridCols = "xl:grid-cols-4";
                } else if (descriptorCount === 6) {
                  // 3 columns for 6
                  gridCols = "grid-cols-1";
                  mdGridCols = "md:grid-cols-2";
                  lgGridCols = "lg:grid-cols-3";
                  xlGridCols = "";
                } else if (descriptorCount === 5) {
                  // 3 columns for 5
                  gridCols = "grid-cols-1";
                  mdGridCols = "md:grid-cols-2";
                  lgGridCols = "lg:grid-cols-3";
                  xlGridCols = "";
                } else if (descriptorCount === 4) {
                  // 2 columns for 4
                  gridCols = "grid-cols-1";
                  mdGridCols = "md:grid-cols-2";
                  lgGridCols = "";
                  xlGridCols = "";
                } else if (descriptorCount === 3) {
                  // 3 columns for 3
                  gridCols = "grid-cols-1";
                  mdGridCols = "md:grid-cols-2";
                  lgGridCols = "lg:grid-cols-3";
                  xlGridCols = "";
                }

                const gridClass = [
                  "grid",
                  gridCols,
                  mdGridCols,
                  lgGridCols,
                  xlGridCols,
                  "gap-4",
                ]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <div className={gridClass}>
                    {criterion.level_descriptors?.map((descriptor, idx) => {
                      const score = criterion.level_scores?.[idx] || 0;
                      const maxScore = Math.max(
                        ...(criterion.level_scores || [0]),
                      );
                      const minScore = Math.min(
                        ...(criterion.level_scores || [0]),
                      );

                      // Calculate color based on score relative to min/max
                      const getCardColors = (
                        score: number,
                        min: number,
                        max: number,
                      ) => {
                        if (max === min)
                          return {
                            bgGradient: "from-gray-50 to-gray-100",
                            borderColor: "border-gray-200",
                            badgeGradient: "from-gray-500 to-gray-600",
                          };

                        const ratio = (score - min) / (max - min);

                        if (ratio >= 0.8)
                          return {
                            bgGradient: "from-green-50 to-emerald-100",
                            borderColor: "border-green-200",
                            badgeGradient: "from-green-500 to-emerald-600",
                          };
                        else if (ratio >= 0.6)
                          return {
                            bgGradient: "from-lime-50 to-green-100",
                            borderColor: "border-lime-200",
                            badgeGradient: "from-lime-500 to-green-600",
                          };
                        else if (ratio >= 0.4)
                          return {
                            bgGradient: "from-yellow-50 to-amber-100",
                            borderColor: "border-yellow-200",
                            badgeGradient: "from-yellow-500 to-amber-600",
                          };
                        else if (ratio >= 0.2)
                          return {
                            bgGradient: "from-orange-50 to-red-100",
                            borderColor: "border-orange-200",
                            badgeGradient: "from-orange-500 to-red-600",
                          };
                        else
                          return {
                            bgGradient: "from-red-50 to-red-100",
                            borderColor: "border-red-200",
                            badgeGradient: "from-red-500 to-red-600",
                          };
                      };

                      const colors = getCardColors(score, minScore, maxScore);

                      return (
                        <div
                          key={idx}
                          className={`bg-gradient-to-br ${colors.bgGradient} dark:from-gray-800 dark:to-gray-700 border ${colors.borderColor} dark:border-gray-600 rounded-lg p-4 hover:shadow-lg transition-all duration-200 transform hover:scale-105`}
                        >
                          {/* Level Score */}
                          <div className="flex items-center justify-center mb-3">
                            <div
                              className={`bg-gradient-to-r ${colors.badgeGradient} text-white px-4 py-2 rounded-full shadow-md`}
                            >
                              <Text variant="span" size="lg" weight="bold">
                                {score}%
                              </Text>
                            </div>
                          </div>

                          {/* Performance Label */}
                          {rubric.performanceLevelNames &&
                            rubric.performanceLevelNames[idx] && (
                              <div className="text-center mb-3">
                                <Text
                                  variant="span"
                                  size="xs"
                                  weight="medium"
                                  className="text-gray-600 dark:text-gray-400 uppercase tracking-wide"
                                >
                                  {rubric.performanceLevelNames[idx]}
                                </Text>
                              </div>
                            )}

                          {/* Level Description */}
                          <div className="prose prose-sm max-w-none dark:prose-invert">
                            <Markdown className="whitespace-pre-wrap text-sm">
                              {descriptor}
                            </Markdown>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      )}
      {/* Holistic Rubric Section */}
      {rubric.rubricType === "HOLISTIC" && rubric.levelDescriptions && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm">
            {/* Holistic Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <Text
                  variant="h3"
                  size="lg"
                  weight="semibold"
                  className="text-gray-900 dark:text-gray-100 mb-2"
                >
                  {tC(contentLanguage, "rubric.type.holistic")}
                </Text>
                <Text
                  variant="p"
                  size="sm"
                  className="text-gray-600 dark:text-gray-400"
                >
                  {tC(contentLanguage, "rubric.type.holisticDesc")}
                </Text>
              </div>
            </div>

            {/* Levels Grid */}
            {(() => {
              const descriptorCount = rubric.levelDescriptions?.length || 0;
              let gridCols = "grid-cols-1";
              let mdGridCols = "md:grid-cols-2";
              let lgGridCols = "lg:grid-cols-3";
              let xlGridCols = "";

              // Determine grid columns based on descriptor count
              if (descriptorCount === 7) {
                // 4 columns for 7
                gridCols = "grid-cols-1";
                mdGridCols = "md:grid-cols-2";
                lgGridCols = "lg:grid-cols-3";
                xlGridCols = "xl:grid-cols-4";
              } else if (descriptorCount === 6) {
                // 3 columns for 6
                gridCols = "grid-cols-1";
                mdGridCols = "md:grid-cols-2";
                lgGridCols = "lg:grid-cols-3";
                xlGridCols = "";
              } else if (descriptorCount === 5) {
                // 3 columns for 5
                gridCols = "grid-cols-1";
                mdGridCols = "md:grid-cols-2";
                lgGridCols = "lg:grid-cols-3";
                xlGridCols = "";
              } else if (descriptorCount === 4) {
                // 2 columns for 4
                gridCols = "grid-cols-1";
                mdGridCols = "md:grid-cols-2";
                lgGridCols = "";
                xlGridCols = "";
              } else if (descriptorCount === 3) {
                // 3 columns for 3
                gridCols = "grid-cols-1";
                mdGridCols = "md:grid-cols-2";
                lgGridCols = "lg:grid-cols-3";
                xlGridCols = "";
              }

              const gridClass = [
                "grid",
                gridCols,
                mdGridCols,
                lgGridCols,
                xlGridCols,
                "gap-4",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <div className={gridClass}>
                  {rubric.levelDescriptions.map((description, index) => {
                    const scoreRange = rubric.scoreRanges?.[index];
                    const minScore = scoreRange?.min || 0;
                    const maxScore = scoreRange?.max || 0;
                    const avgScore = (minScore + maxScore) / 2;

                    // Get max possible score to calculate relative performance
                    const allMaxScores =
                      rubric.scoreRanges
                        ?.map((range) => range.max)
                        .filter((score) => score !== undefined) || [];
                    const allMinScores =
                      rubric.scoreRanges
                        ?.map((range) => range.min)
                        .filter((score) => score !== undefined) || [];
                    const totalMaxScore =
                      allMaxScores.length > 0 ? Math.max(...allMaxScores) : 100;
                    const totalMinScore =
                      allMinScores.length > 0 ? Math.min(...allMinScores) : 0;

                    // Calculate color based on average score relative to total range
                    const getCardColors = (
                      avgScore: number,
                      totalMin: number,
                      totalMax: number,
                    ) => {
                      if (totalMax === totalMin)
                        return {
                          bgGradient: "from-gray-50 to-gray-100",
                          borderColor: "border-gray-200",
                          badgeGradient: "from-gray-500 to-gray-600",
                        };

                      const ratio =
                        (avgScore - totalMin) / (totalMax - totalMin);

                      if (ratio >= 0.8)
                        return {
                          bgGradient: "from-green-50 to-emerald-100",
                          borderColor: "border-green-200",
                          badgeGradient: "from-green-500 to-emerald-600",
                        };
                      else if (ratio >= 0.6)
                        return {
                          bgGradient: "from-lime-50 to-green-100",
                          borderColor: "border-lime-200",
                          badgeGradient: "from-lime-500 to-green-600",
                        };
                      else if (ratio >= 0.4)
                        return {
                          bgGradient: "from-yellow-50 to-amber-100",
                          borderColor: "border-yellow-200",
                          badgeGradient: "from-yellow-500 to-amber-600",
                        };
                      else if (ratio >= 0.2)
                        return {
                          bgGradient: "from-orange-50 to-red-100",
                          borderColor: "border-orange-200",
                          badgeGradient: "from-orange-500 to-red-600",
                        };
                      else
                        return {
                          bgGradient: "from-red-50 to-red-100",
                          borderColor: "border-red-200",
                          badgeGradient: "from-red-500 to-red-600",
                        };
                    };

                    const colors = getCardColors(
                      avgScore,
                      totalMinScore,
                      totalMaxScore,
                    );

                    return (
                      <div
                        key={index}
                        className={`bg-gradient-to-br ${colors.bgGradient} dark:from-gray-800 dark:to-gray-700 border ${colors.borderColor} dark:border-gray-600 rounded-lg p-4 hover:shadow-lg transition-all duration-200 transform hover:scale-105`}
                      >
                        {/* Level Number and Score Range */}
                        <div className="flex items-center justify-center mb-3">
                          <div
                            className={`bg-gradient-to-r ${colors.badgeGradient} text-white px-4 py-2 rounded-full shadow-md`}
                          >
                            <Text variant="span" size="sm" weight="bold">
                              {scoreRange
                                ? `${minScore}-${maxScore}`
                                : `${index + 1}`}
                            </Text>
                          </div>
                        </div>

                        {/* Performance Label */}
                        {rubric.performanceLevelNames &&
                          rubric.performanceLevelNames[index] && (
                            <div className="text-center mb-3">
                              <Text
                                variant="span"
                                size="xs"
                                weight="medium"
                                className="text-gray-600 dark:text-gray-400 uppercase tracking-wide"
                              >
                                {rubric.performanceLevelNames[index]}
                              </Text>
                            </div>
                          )}

                        {/* Level Description */}
                        <div className="prose prose-sm max-w-none dark:prose-invert">
                          <Markdown className="whitespace-pre-wrap text-sm">
                            {description}
                          </Markdown>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        hideMadlenLogoOptions
        documentComponent={
          <RubricCardPDF
            customizationSettings={(customizationSettings as any) || {}}
            rubric={{ ...rubric, imageUrl: finalImageUrl }}
          />
        }
      />
    </div>
  );
};

export default RubricCard;
